// src/pages/MyProfile/components/AvatarCoverUploader.jsx
import React, { useState, useRef } from "react";
import {
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    CircularProgress // Tambahkan CircularProgress
} from "@mui/material";
import { PhotoCamera } from '@mui/icons-material';
import { ref, update } from "firebase/database";

const AvatarCoverUploader = ({ user, theme, database, currentAvatarCoverUrl, setSnackbar }) => {
    const avatarCoverFileInputRef = useRef(null);

    // State untuk dialog pemrosesan gambar (drag & crop) untuk avatar cover
    const [avatarCoverToProcess, setAvatarCoverToProcess] = useState(null);
    const [openAvatarCoverProcessDialog, setOpenAvatarCoverProcessDialog] = useState(false);
    const [avatarCoverOffset, setAvatarCoverOffset] = useState({ x: 0, y: 0 });
    const [isDraggingAvatarCover, setIsDraggingAvatarCover] = useState(false);
    const [startDragXCover, setStartDragXCover] = useState(0);
    const [startDragYCover, setStartDragYCover] = useState(0);
    const [scaledAvatarCoverDimensions, setScaledAvatarCoverDimensions] = useState({ width: 0, height: 0 });
    const avatarCoverPreviewRef = useRef(null);
    const [processingCover, setProcessingCover] = useState(false); // New state for loading

    const handleAvatarCoverClick = () => {
        avatarCoverFileInputRef.current.click();
    };

    const handleAvatarCoverUpload = (event) => {
        const file = event.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                const img = new Image();
                img.src = reader.result;
                img.onload = () => {
                    const viewportWidth = 600;
                    const viewportHeight = 250;

                    const scale = Math.max(viewportWidth / img.width, viewportHeight / img.height);

                    const calculatedScaledWidth = img.width * scale;
                    const calculatedScaledHeight = img.height * scale;

                    setAvatarCoverToProcess(reader.result);
                    setOpenAvatarCoverProcessDialog(true);

                    setScaledAvatarCoverDimensions({ width: calculatedScaledWidth, height: calculatedScaledHeight });
                    setAvatarCoverOffset({
                        x: (viewportWidth - calculatedScaledWidth) / 2,
                        y: (viewportHeight - calculatedScaledHeight) / 2
                    });
                };
            };
            reader.readAsDataURL(file);
        }
    };

    const handleMouseDownCover = (e) => {
        setIsDraggingAvatarCover(true);
        setStartDragXCover(e.clientX - avatarCoverOffset.x);
        setStartDragYCover(e.clientY - avatarCoverOffset.y);
    };

    const handleMouseMoveCover = (e) => {
        if (!isDraggingAvatarCover) return;

        const newX = e.clientX - startDragXCover;
        const newY = e.clientY - startDragYCover;

        const viewportWidth = 600;
        const viewportHeight = 250;

        const maxX = Math.max(0, scaledAvatarCoverDimensions.width - viewportWidth);
        const maxY = Math.max(0, scaledAvatarCoverDimensions.height - viewportHeight);

        const clampedX = Math.max(-maxX, Math.min(0, newX));
        const clampedY = Math.max(-maxY, Math.min(0, newY));

        setAvatarCoverOffset({ x: clampedX, y: clampedY });
    };

    const handleMouseUpCover = () => {
        setIsDraggingAvatarCover(false);
    };

    const handleSaveProcessedAvatarCover = async () => {
        if (!avatarCoverToProcess || !user) {
            setSnackbar({ open: true, message: "Tidak ada gambar cover untuk disimpan.", severity: "warning" });
            return;
        }

        setProcessingCover(true); // Start loading

        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const finalCoverWidth = 800;
        const finalCoverHeight = 250;
        const viewportWidth = 600;
        const viewportHeight = 250;

        canvas.width = finalCoverWidth;
        canvas.height = finalCoverHeight;

        const img = new Image();
        img.src = avatarCoverToProcess;

        img.onload = async () => {
            const displayScale = Math.max(viewportWidth / img.width, viewportHeight / img.height);

            const sourceX = -avatarCoverOffset.x / displayScale;
            const sourceY = -avatarCoverOffset.y / displayScale;

            const sourceWidth = viewportWidth / displayScale;
            const sourceHeight = viewportHeight / displayScale;

            ctx.drawImage(
                img,
                sourceX,
                sourceY,
                sourceWidth,
                sourceHeight,
                0, 0,
                finalCoverWidth,
                finalCoverHeight
            );

            const croppedBase64 = canvas.toDataURL('image/jpeg', 0.9);

            try {
                const userRef = ref(database, `db_user/tb_user/${user.uid}`);
                await update(userRef, { avatar_cover: croppedBase64 });
                setSnackbar({ open: true, message: "Cover avatar berhasil diperbarui!", severity: "success" });
                setOpenAvatarCoverProcessDialog(false);
                setAvatarCoverToProcess(null);
                setAvatarCoverOffset({ x: 0, y: 0 });
                setScaledAvatarCoverDimensions({ width: 0, height: 0 });
            } catch (error) {
                console.error("Error updating avatar cover:", error);
                setSnackbar({ open: true, message: "Gagal memperbarui cover avatar.", severity: "error" });
            } finally {
                setProcessingCover(false); // Stop loading
            }
        };
    };

    const handleCloseAvatarCoverProcessDialog = () => {
        setOpenAvatarCoverProcessDialog(false);
        setAvatarCoverToProcess(null);
        setAvatarCoverOffset({ x: 0, y: 0 });
        setIsDraggingAvatarCover(false);
        setScaledAvatarCoverDimensions({ width: 0, height: 0 });
    };

    return (
        <Box sx={{
            height: 250,
            backgroundColor: theme.palette.primary.light,
            backgroundImage: currentAvatarCoverUrl ? `url(${currentAvatarCoverUrl})` : 'linear-gradient(to right top, #6dd5ed, #2193b0)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            borderRadius: '8px 8px 0 0',
            position: 'relative',
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'flex-start',
            p: 2,
            '&:hover .upload-cover-icon-overlay': {
                opacity: 1,
            }
        }}>
            <Box sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                zIndex: 0,
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: '-50%',
                    left: '-20%',
                    width: '120%',
                    height: '120%',
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    transform: 'rotate(20deg)',
                    borderRadius: '50%',
                },
                '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: '-50%',
                    right: '-20%',
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    transform: 'rotate(-30deg)',
                    borderRadius: '50%',
                }
            }} />
            <input
                type="file"
                accept="image/jpeg, image/png"
                ref={avatarCoverFileInputRef}
                onChange={handleAvatarCoverUpload}
                style={{ display: 'none' }}
            />
            <Box
                className="upload-cover-icon-overlay"
                sx={{
                    position: 'absolute',
                    top: '12px',
                    right: '12px',
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    bgcolor: 'white',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    opacity: 0,
                    transition: 'opacity 0.3s',
                    cursor: 'pointer',
                    border: `2px solid ${theme.palette.background.paper}`,
                    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.2)',
                    zIndex: 1,
                }}
                onClick={handleAvatarCoverClick}
            >
                <PhotoCamera sx={{ fontSize: 16, color: theme.palette.primary.main }} />
            </Box>

            {/* Dialog untuk pemrosesan gambar (drag & crop) untuk avatar cover */}
            <Dialog open={openAvatarCoverProcessDialog} onClose={handleCloseAvatarCoverProcessDialog} maxWidth="md" fullWidth>
                <DialogTitle>Sesuaikan Cover Avatar</DialogTitle>
                <DialogContent dividers>
                    {avatarCoverToProcess && (
                        <Box
                            sx={{
                                width: '100%',
                                maxWidth: 600,
                                height: 250,
                                overflow: 'hidden',
                                position: 'relative',
                                border: '1px solid #ccc',
                                cursor: isDraggingAvatarCover ? 'grabbing' : 'grab',
                                mx: 'auto',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                            onMouseDown={handleMouseDownCover}
                            onMouseMove={handleMouseMoveCover}
                            onMouseUp={handleMouseUpCover}
                            onMouseLeave={handleMouseUpCover}
                        >
                            <img
                                ref={avatarCoverPreviewRef}
                                src={avatarCoverToProcess}
                                alt="Pratinjau Cover"
                                style={{
                                    position: 'absolute',
                                    left: avatarCoverOffset.x,
                                    top: avatarCoverOffset.y,
                                    width: scaledAvatarCoverDimensions.width,
                                    height: scaledAvatarCoverDimensions.height,
                                    userSelect: 'none',
                                    pointerEvents: 'none',
                                }}
                            />
                        </Box>
                    )}
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
                        Seret gambar untuk menyesuaikan posisinya.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseAvatarCoverProcessDialog} disabled={processingCover}>Batal</Button>
                    <Button onClick={handleSaveProcessedAvatarCover} variant="contained" color="primary" disabled={processingCover}>
                        {processingCover ? <CircularProgress size={24} color="inherit" /> : "Simpan Cover"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default AvatarCoverUploader;
